import { useState } from 'react';
import { FilterSidebarLayout, OptimusProvider, QueryBoundary, useBreadcrumbs } from '@mews-commander/ui';
import { Container, LoadingOverlay, PAGINATION_SIZES, StandaloneTable, useTableState } from '@optimus-web/core';
import { ArticlePath, createGuideLink } from '@mews-commander/enums';
import { lt } from '@mews/framework';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { filterSidebar } from './filterSidebar';
import { defaultFilterSchema, FilterFormValues } from './filterSchema';
import { useDefaultFilterValues } from './useDefaultFilterValues';
import { simpleColumns, SwedishTableReport } from './TableDefinition';
import { useHasSwedishIntegrationEnabled } from './useHasSwedishIntegrationEnabled';
import { useGetSwedishCashRegister } from './useGetSwedishCashRegister';

export const SwedishFiscalReport: React.FC = () => {
    const { data: enterpriseIntegrations, isLoading: isLoadingIntegration } = useHasSwedishIntegrationEnabled();
    const { data: cashRegisters, isLoading: isLoadingCashRegisters } = useGetSwedishCashRegister();

    const defaultFilterValues = useDefaultFilterValues({
        cashRegisters,
        enterpriseIntegrations,
    });

    const isLoadingDefaultValues = isLoadingIntegration || isLoadingCashRegisters;
    if (isLoadingDefaultValues) {
        return <LoadingOverlay />;
    }

    return <SwedishFiscalReportPage defaultFilterValues={defaultFilterValues} />;
};

interface Props {
    defaultFilterValues: FilterFormValues;
}
const SwedishFiscalReportPage: React.FC<Props> = ({ defaultFilterValues }) => {
    const breadcrumbs = useBreadcrumbs();

    const guideLink = createGuideLink(ArticlePath.HowToGenerateSwedishCashRegisterReports);

    const [filterValues, setFilterValues] = useState(defaultFilterValues);
    const [tableState, setTableState] = useTableState<SwedishTableReport>({
        pagination: {
            pageIndex: 0,
            pageSize: PAGINATION_SIZES[25],
            show: true,
        },
    });

    const { data, isPending } = useRepositoryQuery('swedishCashRegisterFiscalDay', 'getAll', {
        integrationId: filterValues.swedishIntegrationId,
        swedishCashRegisterId: filterValues.cashRegisterId,
        date: {
            start: filterValues.dateFrom,
            end: filterValues.dateTo,
        },
    });

    return (
        <OptimusProvider density="spacious">
            <FilterSidebarLayout
                breadcrumbs={breadcrumbs}
                guideLink={guideLink}
                onFilterSubmit={(values) => setFilterValues(values)}
                filterSchema={defaultFilterSchema}
                defaultFilterValues={defaultFilterValues}
                filterSidebar={filterSidebar}
                loading={isPending}
            >
                <Container padding={['200', '400']}>
                    <QueryBoundary loader="inline">
                        <StandaloneTable
                            uniqueRowId="date"
                            amountOfRows={data?.length || 0}
                            data={data || []}
                            loading={isPending}
                            columns={simpleColumns}
                            state={tableState}
                            onStateChange={setTableState}
                            emptyState={{
                                primaryText: lt('SwedishCashRegisterReportEmptyTablePrimaryText'),
                                secondaryText: lt('SwedishCashRegisterReportEmptyTableSecondaryText'),
                            }}
                        />
                    </QueryBoundary>
                </Container>
            </FilterSidebarLayout>
        </OptimusProvider>
    );
};
